import React from "react";
import PropTypes from "prop-types";
import {
  CardContainer,
  PlanHeader,
  PlanIconContainer,
  PlanIcon,
  PlanTitle,
  PriceContainer,
  PriceRow,
  OriginalPrice,
  DiscountedRow,
  Price,
  PriceInterval,
  ValidityPeriod,
  AnnualBillingText
} from "./SubscriptionCard.styled";
import { formatAnnualTotal } from "../../../../utils/annualPricing";

/**
 * A card component for displaying subscription plan information
 */
const SubscriptionCard = ({
  planType,
  planName,
  price,
  originalPrice,
  interval = "/month",
  validityLabel,
  validityDate,
  isActive = false,
  durationDisplay = null,
  yearlyPrice = null
}) => {
  const iconMap = {
    premium: "P",
    essential: "E",
    prosuite: "Ps",
    free: "F",
  };

  // Simply check if this is an annual subscription using durationDisplay
  const isAnnualSubscription = durationDisplay === "Annually";

  const renderPrices = () => {
    if (originalPrice) {
      // Render discounted price with original price side by side
      return (
        <PriceRow>
          <OriginalPrice>${originalPrice}</OriginalPrice>
          <DiscountedRow>
            <Price>${price}</Price>
            <PriceInterval>{interval}</PriceInterval>
          </DiscountedRow>
        </PriceRow>
      );
    }
    
    // Just render the regular price
    return (
      <DiscountedRow>
        <Price>${price}</Price>
        <PriceInterval>{interval}</PriceInterval>
      </DiscountedRow>
    );
  };

  return (
    <CardContainer active={isActive}>
      <PlanHeader>
        <PlanIconContainer type={planType}>
          <PlanIcon>{iconMap[planType] || planType.charAt(0)}</PlanIcon>
        </PlanIconContainer>
        <PlanTitle active={isActive}>
          {planName} {isAnnualSubscription ? "(Annual Subscription)" : "(Monthly Subscription)"}
        </PlanTitle>
      </PlanHeader>

      <PriceContainer>
        {renderPrices()}

        {isAnnualSubscription && price !== "0" && (
          <AnnualBillingText>
            {/* PLQA-C-005: yearlyPrice is only ever an explicit discount
                override (see UpgradeModal/DowngradeModal) -- the plain,
                undiscounted total is always derived from `price` via the
                same formatAnnualTotal helper the pricing card uses, so the
                same plan never shows two different annual totals. */}
            ${yearlyPrice || formatAnnualTotal(price)} billed annually
          </AnnualBillingText>
        )}
        {validityDate && (
          <ValidityPeriod>
            {validityLabel} {validityDate}
          </ValidityPeriod>
        )}
      </PriceContainer>
    </CardContainer>
  );
};

SubscriptionCard.propTypes = {
  planType: PropTypes.string.isRequired,
  planName: PropTypes.string.isRequired,
  price: PropTypes.string.isRequired,
  originalPrice: PropTypes.string,
  interval: PropTypes.string,
  validityLabel: PropTypes.string.isRequired,
  validityDate: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  durationDisplay: PropTypes.string,
  yearlyPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default SubscriptionCard; 